// Language utilities for internationalization
// Language is determined by URL parameter: ?lang=cn or ?lang=en (default: en)

// Get language from URL parameters
export const getLanguageFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const lang = urlParams.get('lang');
  return lang === 'en' ? 'en' : 'cn'; // Default to 'cn' if not 'en'
};

// Language translations
const translations = {
  en: {
    // App
    'app.title': 'Product Customizer',
    
    // Theme Selection
    'theme.title': 'Select Theme',
    'theme.subtitle': 'Choose a theme for your stickers',
    'theme.back': 'Back to Themes',
    'theme.noThemes': 'No themes available',
    'theme.noStickers': 'No stickers available in this theme',
    'theme.stickerSets': 'sticker sets',
    'theme.selected': '✓ Selected',
    'theme.cancel': 'Cancel',
    'theme.confirm': 'Confirm',
    'theme.continue': 'Continue',
    'theme.chooseOptional': 'Choose a sticker set',
    'theme.chooseRequired': 'Choose a sticker set to continue',
    'theme.testMode': 'Test Mode',
    'theme.pleaseSelect': 'Please select a sticker set to continue',
    'theme.pleaseSelectFromTheme': 'Please select a sticker set from the theme selection to view stickers.',

    // Stickers Tab
    'stickers.title': 'Stickers',
    'stickers.selectTheme': 'Select Theme',
    'stickers.loading': 'Loading stickers...',
    'stickers.loadingThemes': 'Loading themes...',
    'stickers.noStickers': 'No stickers available',
    'stickers.dragToAdd': 'Drag stickers to canvas',

    // Text Tab
    'text.title': 'Text',
    'text.addText': 'Add Text',
    'text.updateText': 'Update Text',
    'text.textContent': 'Text Content',
    'text.fontFamily': 'Font Family',
    'text.fontSize': 'Font Size',
    'text.fontColor': 'Font Color',
    'text.bold': 'Bold',
    'text.italic': 'Italic',
    'text.underline': 'Underline',
    'text.alignment': 'Alignment',
    'text.left': 'Left',
    'text.center': 'Center',
    'text.right': 'Right',
    'text.loadingFonts': 'Loading fonts...',
    'text.loadingFont': 'Loading font...',
    'text.refreshFonts': 'Refresh Fonts',
    'text.enterText': 'Enter your text',
    'text.selectFont': 'Select a font...',
    'text.previewText': 'Preview Text',
    // Text styling labels
    'text.textStroke': 'Text Stroke',
    'text.backgroundStroke': 'Background Stroke',
    'text.color': 'Color',
    'text.width': 'Width',

    // Upload Tab
    'upload.title': 'Upload',
    'upload.uploadFiles': 'Upload Files',
    'upload.processing': 'Processing',
    'upload.uploading': 'Uploading...',
    'upload.dragDrop': 'Drag and drop files here',
    'upload.clickBrowse': 'or click to browse',
    'upload.supportedFormats': 'Supported formats: JPG, PNG, GIF, SVG',
    'upload.maxFileSize': 'Max file size: 5MB',
    'upload.tip': 'Images will be added to your design with proper scaling and positioning',
    'upload.recentlyUploaded': 'Recently Uploaded',
    'upload.subtitle': 'Add images to your design',
    'upload.selectFiles': 'Select Files',
    'upload.formats': 'Supported Formats',
    'upload.formatsList': 'JPG, PNG, GIF, SVG',
    'upload.maxSize': 'Max File Size',
    'upload.sizeLimit': '5MB per file',
    'upload.recommendedSize': 'Recommended: under 5MB per file for best performance',
    'upload.recommendedFormats': 'Recommended formats: PNG, JPG, WEBP, SVG',

    // Background Tab
    'background.title': 'Background',
    'background.transparent': 'Transparent',
    'background.white': 'White',
    'background.black': 'Black',
    'background.gray': 'Gray',
    'background.blue': 'Blue',
    'background.custom': 'Custom',
    'background.apply': 'Apply',
    'background.color': 'Background Color',
    'background.pattern': 'Background Pattern',
    'background.image': 'Background Image',
    'background.none': 'None',

    // Layer Tab
    'layer.title': 'Layers',
    'layer.layers': 'Layers',
    'layer.productLayer': 'Product Layer',
    'layer.designLayer': 'Design Layer',
    'layer.caseLayer': 'Case Layer',
    'layer.guidelineLayer': 'Guideline Layer',
    'layer.maskLayer': 'Mask Layer',
    'layer.productDesc': 'Contains the main product design elements',
    'layer.designDesc': 'Contains your custom designs and artwork',
    'layer.caseDesc': 'Contains the product case or container',
    'layer.guidelineDesc': 'Contains alignment guides and boundaries',
    'layer.hideLayer': 'Hide Layer',
    'layer.showLayer': 'Show Layer',
    'layer.lockLayer': 'Lock Layer',
    'layer.unlockLayer': 'Unlock Layer',
    'layer.layerInformation': 'Layer Information',
    'layer.fixedLayerSystem': 'This product uses a fixed layer system:',
    'layer.productLayerDesc': 'Top layer for your product elements',
    'layer.designLayerDesc': 'For your custom designs and artwork',
    'layer.maskLayerDesc': 'Controls which areas are visible',
    'layer.caseLayerDesc': 'Product case or container',
    'layer.guidelineLayerDesc': 'Shows boundaries and guides',
    'layer.toggleInstructions': 'Toggle visibility or lock status using the controls.',
    'layer.management': 'Layer Management',
    'layer.moveUp': 'Move Up',
    'layer.moveDown': 'Move Down',
    'layer.delete': 'Delete Layer',
    'layer.duplicate': 'Duplicate',

    // Sidebar
    'sidebar.stickers': 'Stickers',
    'sidebar.text': 'Text',
    'sidebar.upload': 'Upload',
    'sidebar.templates': 'Templates',

    // Layer Modal
    'layer.title': 'Layer Manager',
    'layer.noLayers': 'No layers available',
    'layer.close': 'Close',
    'layer.lockLayer': 'Lock layer',
    'layer.unlockLayer': 'Unlock layer',
    'layer.deleteLayer': 'Delete layer',
    'layer.zIndex': 'z:',
    
    // Product Tab
    'product.title': 'Product',
    'product.noData': 'No product data available',
    'product.search': 'Search products...',
    'product.apple': 'Apple iPhone',
    'product.samsung': 'Samsung Galaxy',
    'product.other': 'Other',
    'product.noResults': 'No products found matching your search.',
    'product.currentSelection': 'Current Selection',
    'product.model': 'Model',
    'product.color': 'Color',
    'product.selectProduct': 'Select Product',
    'product.selectColor': 'Select Color',
    'product.selectView': 'Select View',
    'product.front': 'Front',
    'product.back': 'Back',
    'product.left': 'Left',
    'product.right': 'Right',
    'product.addToCart': 'Add to Cart',
    'batch.title': 'Batch Order',
    'batch.addItem': 'Add Item',
    'batch.noItems': 'No items yet',

    // Exported Images Panel
    'export.title': 'Exported Product Images',
    'export.close': 'Close panel',
    'export.downloadView': 'Download View',
    'export.preview': 'Preview',
    'export.design': 'Design',
    'export.step': 'Step',
    'export.noPreview': 'No preview available',
    'export.noDesign': 'No design available',
    'export.default': 'Default',
    'product.download': 'Download',
    'product.preview': 'Preview',

    'kiosk.selectProductTitle': 'Please select your product',
    'kiosk.selectProductModelTitle': 'Select model',

    // General
    'general.loading': 'Loading...',
    'general.error': 'Error',
    'general.retry': 'Retry',
    'general.close': 'Close',
    'general.stay': 'Stay',
    'general.save': 'Save',
    'general.of': 'of',
    'general.next': 'Next',
    'general.back': 'Back',
    'general.delete': 'Delete',
    'general.edit': 'Edit',
    'general.add': 'Add',
    'general.remove': 'Remove',
    'general.yes': 'Yes',
    'general.no': 'No',
    'general.ok': 'OK',
    'general.menu': 'Menu',
    'general.selectOption': 'Select an option',
    'general.done': 'Done',
    'general.undo': 'Undo',
    'general.redo': 'Redo',
    'general.layer': 'Layer',
    'general.view3d': '3D View',
    'common.region': 'Region'
    ,
    // Header / general actions
    'general.cancel': 'Cancel',
    'general.confirm': 'Confirm',
    'header.confirmExportTitle': 'Confirm Export',
    'header.confirmExportMessage': 'Are all designs confirmed?',
    'header.backDisabled': 'Back disabled',
    'header.designIndex': 'Design index',
    // Cart / confirmation
    'cart.title': 'Add to Cart',
    'cart.confirmMessage': 'Are you sure you want to add this design to your cart?',
    'cart.confirmYes': 'Yes, Add to Cart',
    'cart.cancel': 'Cancel',
    // Status messages
    'status.preparingDesign': 'Preparing your design...',
    'status.exportingAllViews': 'Exporting all views...',
    'status.uploadingAllDesigns': 'Uploading all designs...',
    'status.addingToCart': 'Adding to cart...',
    'status.addedToCart': 'Added to cart!',
    'status.redirectingToCart': 'Added to cart. Redirecting to cart...',
    'status.redirectCountdown': 'You will be redirected in',
    'status.goToCartNow': 'Go to Cart Now',
    'status.preparingExport': 'Preparing export...',
    'status.exportingPreview': 'Exporting preview...',
    'status.exportingDesign': 'Exporting design...',
    'status.extractingTexture': 'Extracting texture...',
    'status.exportComplete': 'Export complete',
    'status.noImagesToExport': 'No images to export',
    'status.exportReady': 'Export ready',
    'status.testModeUploadComplete': 'Test mode upload complete',
    // Short progress labels for dynamic counters
    'status.exporting': 'Exporting',
    'status.uploading': 'Uploading',
    // Export panel
    'export.addToCart': 'Add to Cart',
    'export.back': 'Back',
    // View names
    'views.front': 'Front',
    'views.back': 'Back',
    'views.left': 'Left',
    'views.right': 'Right',
    'views.top': 'Top',
    'views.bottom': 'Bottom',
    'views.default': 'Default',
    'views.card': 'Card',
    'views.box': 'Box',
    // Errors
    'error.exportingImages': 'Error exporting images. Please try again.',
    'error.preparingExport': 'Error preparing export',
    'error.exportFailed': 'Export failed. Please try again.',
    'error.designSystemNotReady': 'Design system not ready. Please try again.',
    'error.missingVariant': 'Error: Missing product variant',
    'error.failedToGenerateDesign': 'Failed to generate design image. Please try again.',
    'error.failedToAddToCart': 'Failed to add product to cart. Please try again.',
    'error.navigateToCartFailed': 'Failed to open cart. Please tap the cart tab manually.'
  },
  cn: {
    // App
    'app.title': '产品定制器',
    
    // Theme Selection
    'theme.title': '选择主题',
    'theme.subtitle': '为您的贴纸选择一个主题',
    'theme.back': '返回主题',
    'theme.noThemes': '没有可用的主题',
    'theme.noStickers': '此主题中没有可用的贴纸',
    'theme.stickerSets': '贴纸套装',
    'theme.selected': '✓ 已选择',
    'theme.cancel': '取消',
    'theme.confirm': '确认',
    'theme.continue': '继续',
    'theme.chooseOptional': '选择贴纸套装',
    'theme.chooseRequired': '选择贴纸套装以继续',
    'theme.testMode': '测试模式',
    'theme.pleaseSelect': '请选择贴纸套装以继续',
    'theme.pleaseSelectFromTheme': '请从主题选择中选择贴纸套装以查看贴纸。',

    // Stickers Tab
    'stickers.title': '贴纸',
    'stickers.selectTheme': '选择主题',
    'stickers.loading': '加载贴纸中...',
    'stickers.loadingThemes': '加载主题中...',
    'stickers.noStickers': '没有可用的贴纸',
    'stickers.dragToAdd': '拖拽贴纸到画布',

    // Text Tab
    'text.title': '文字',
    'text.addText': '添加文字',
    'text.updateText': '更新文字',
    'text.textContent': '文字内容',
    'text.fontFamily': '字体',
    'text.fontSize': '字号',
    'text.fontColor': '字体颜色',
    'text.bold': '粗体',
    'text.italic': '斜体',
    'text.underline': '下划线',
    'text.alignment': '对齐方式',
    'text.left': '左对齐',
    'text.center': '居中',
    'text.right': '右对齐',
    'text.loadingFonts': '加载字体中...',
    'text.loadingFont': '加载字体中...',
    'text.refreshFonts': '刷新字体',
    'text.enterText': '输入您的文本',
    'text.selectFont': '选择字体...',
    'text.previewText': '预览文本',
    // Text styling labels
    'text.textStroke': '文字描边',
    'text.backgroundStroke': '背景描边',
    'text.color': '颜色',
    'text.width': '宽度',

    // Upload Tab
    'upload.title': '上传',
    'upload.uploadFiles': '上传文件',
    'upload.processing': '处理中',
    'upload.uploading': '上传中...',
    'upload.dragDrop': '拖拽文件到这里',
    'upload.clickBrowse': '或点击浏览',
    'upload.supportedFormats': '支持格式：JPG、PNG、GIF、SVG',
    'upload.maxFileSize': '最大文件大小：5MB',
    'upload.tip': '图片将以适当的缩放和位置添加到您的设计中',
    'upload.recentlyUploaded': '最近上传',
    'upload.subtitle': '将图片添加到您的设计中',
    'upload.selectFiles': '选择文件',
    'upload.formats': '支持格式',
    'upload.formatsList': 'JPG、PNG、GIF、SVG',
    'upload.maxSize': '文件大小',
    'upload.sizeLimit': '每个文件 5MB',
    'upload.recommendedSize': '建议：每个文件小于 5MB，体验更流畅',
    'upload.recommendedFormats': '建议格式：PNG, JPG, WEBP, SVG',

    // Background Tab
    'background.title': '背景',
    'background.transparent': '透明',
    'background.white': '白色',
    'background.black': '黑色',
    'background.gray': '灰色',
    'background.blue': '蓝色',
    'background.custom': '自定义',
    'background.apply': '应用',
    'background.color': '背景颜色',
    'background.pattern': '背景图案',
    'background.image': '背景图片',
    'background.none': '无',

    // Layer Tab
    'layer.title': '图层',
    'layer.layers': '图层',
    'layer.productLayer': '产品图层',
    'layer.designLayer': '设计图层',
    'layer.caseLayer': '外壳图层',
    'layer.guidelineLayer': '参考线图层',
    'layer.maskLayer': '遮罩图层',
    'layer.productDesc': '包含主要产品设计元素',
    'layer.designDesc': '包含您的自定义设计和艺术作品',
    'layer.caseDesc': '包含产品外壳或容器',
    'layer.guidelineDesc': '包含对齐参考线和边界',
    'layer.hideLayer': '隐藏图层',
    'layer.showLayer': '显示图层',
    'layer.lockLayer': '锁定图层',
    'layer.unlockLayer': '解锁图层',
    'layer.layerInformation': '图层信息',
    'layer.fixedLayerSystem': '此产品使用固定图层系统：',
    'layer.productLayerDesc': '产品元素的顶层',
    'layer.designLayerDesc': '用于您的自定义设计和艺术作品',
    'layer.maskLayerDesc': '控制可见区域',
    'layer.caseLayerDesc': '产品外壳或容器',
    'layer.guidelineLayerDesc': '显示边界和参考线',
    'layer.toggleInstructions': '使用控件切换可见性或锁定状态。',
    'layer.management': '图层管理',
    'layer.moveUp': '上移',
    'layer.moveDown': '下移',
    'layer.delete': '删除图层',
    'layer.duplicate': '复制',

    // Sidebar
    'sidebar.stickers': '贴纸',
    'sidebar.text': '文字',
    'sidebar.upload': '上传',
    'sidebar.templates': '模板',

    // Layer Modal
    'layer.title': '图层管理器',
    'layer.noLayers': '没有可用的图层',
    'layer.close': '关闭',
    'layer.lockLayer': '锁定图层',
    'layer.unlockLayer': '解锁图层',
    'layer.deleteLayer': '删除图层',
    'layer.zIndex': '层级:',
    
    // Product Tab
    'product.title': '产品',
    'product.noData': '没有可用的产品数据',
    'product.searchModels': '搜索型号...',
    'product.all': '全部',
    'product.iphone': 'iPhone',
    'product.samsung': '三星Galaxy',
    'product.other': '其他',
    'product.noResults': '未找到与您的搜索匹配的产品。',
    'product.currentSelection': '当前选择',
    'product.model': '型号',
    'product.color': '颜色',
    'product.selectProduct': '选择产品',
    'product.selectColor': '选择颜色',
    'product.selectView': '选择视图',
    'product.front': '正面',
    'product.back': '背面',
    'product.left': '左侧',
    'product.right': '右侧',
    'product.addToCart': '加入购物车',
    'product.download': '下载',
    'product.preview': '预览',

    'kiosk.selectProductTitle': '請選擇你的產品',
    'kiosk.selectProductModelTitle': '請選擇型號',
    'batch.title': '批量下单',
    'batch.addItem': '添加项目',
    'batch.noItems': '暂无项目',

    // Exported Images Panel
    'export.title': '导出的产品图片',
    'export.close': '关闭面板',
    'export.downloadView': '下载视图',
    'export.preview': '预览',
    'export.design': '设计',
    'export.step': '步骤',
    'export.noPreview': '无预览可用',
    'export.noDesign': '无设计可用',
    'export.default': '默认',

    // General
    'general.loading': '加载中...',
    'general.error': '错误',
    'general.retry': '重试',
    'general.close': '关闭',
    'general.stay': '留在本页',
    'general.save': '保存',
    'general.of': '共',
    'general.next': '下一步',
    'general.back': '返回',
    'general.delete': '删除',
    'general.edit': '编辑',
    'general.add': '添加',
    'general.remove': '移除',
    'general.yes': '是',
    'general.no': '否',
    'general.ok': '确定',
    'general.menu': '菜单',
    'general.selectOption': '选择一个选项',
    'general.done': '完成',
    'general.undo': '撤销',
    'general.redo': '重做',
    'general.layer': '图层',
    'general.view3d': '3D 视图',
    'common.region': '区域'
    ,
    // Header / general actions
    'general.cancel': '取消',
    'general.confirm': '确认',
    'header.confirmExportTitle': '确认导出',
    'header.confirmExportMessage': '所有档案设计是否确认？',
    'header.backDisabled': '无法返回',
    'header.designIndex': '设计序号',
    // Cart / confirmation
    'cart.title': '加入购物车',
    'cart.confirmMessage': '确定将此设计加入购物车吗？',
    'cart.confirmYes': '是的，加入购物车',
    'cart.cancel': '取消',
    // Status messages
    'status.preparingDesign': '正在准备您的设计...',
    'status.exportingAllViews': '正在导出所有视图...',
    'status.uploadingAllDesigns': '正在上传所有设计...',
    'status.addingToCart': '正在加入购物车...',
    'status.addedToCart': '已加入购物车！',
    'status.redirectingToCart': '已加入购物车，正在跳转到购物车页面…',
    'status.redirectCountdown': '即将为您跳转，剩余',
    'status.goToCartNow': '立即前往购物车',
    'status.preparingExport': '正在准备导出...',
    'status.exportingPreview': '正在导出预览...',
    'status.exportingDesign': '正在导出设计...',
    'status.extractingTexture': '正在提取纹理...',
    'status.exportComplete': '导出完成',
    'status.noImagesToExport': '没有可导出的图片',
    'status.exportReady': '导出已就绪',
    'status.testModeUploadComplete': '测试模式上传完成',
    // Short progress labels for dynamic counters
    'status.exporting': '正在导出',
    'status.uploading': '正在上传',
    // Export panel
    'export.addToCart': '加入购物车',
    'export.back': '返回',
    // View names
    'views.front': '正面',
    'views.back': '背面',
    'views.left': '左侧',
    'views.right': '右侧',
    'views.top': '顶部',
    'views.bottom': '底部',
    'views.default': '默认',
    'views.card': '卡片',
    'views.box': '盒子',
    // Errors
    'error.exportingImages': '导出图片时出错。请重试。',
    'error.preparingExport': '准备导出时出错',
    'error.exportFailed': '导出失败。请重试。',
    'error.designSystemNotReady': '设计系统未就绪。请重试。',
    'error.missingVariant': '错误：缺少产品变体',
    'error.failedToGenerateDesign': '生成设计图片失败。请重试。',
    'error.failedToAddToCart': '加入购物车失败。请重试。',
    'error.navigateToCartFailed': '打开购物车失败，请手动点击底部“购物车”标签。'
  }
};

// Get translation function
export const useTranslation = () => {
  const currentLang = getLanguageFromUrl();
  
  const t = (key, defaultValue = key) => {
    return translations[currentLang]?.[key] || translations.cn[key] || defaultValue;
  };

  return { t, currentLang };
};

// Direct translation function (for use outside React components)
export const translate = (key, defaultValue = key) => {
  const language = getLanguageFromUrl();
  return translations[language]?.[key] || defaultValue;
};

// Get current language for use outside of React components
export const getCurrentLanguage = () => {
  return getLanguageFromUrl();
};

// Get language for web resource loading (always returns 'en')
export const getWebResourceLanguage = () => {
  return 'en'; // Always use English for web resource loading
};

// Translate web resource text (always uses English)
export const translateWebResource = (key, defaultValue = key) => {
  return translations['en']?.[key] || defaultValue;
};

const languageUtils = {
  getLanguageFromUrl,
  getCurrentLanguage,
  useTranslation,
  translate,
  getWebResourceLanguage,
  translateWebResource
};

export default languageUtils;
